import dayjs from "dayjs";
import _ from "lodash";

import type {
  Income,
  IncomeStream,
  IncomeVerificationMethod,
  StreamVerificationMethod,
} from "renter/interfaces/api/income";

export const INTERNATIONAL_STREAM_VERIFICATION_METHODS = [
  "ocr-international-employment-letter",
  "ocr-international-paystubs",
  "ocr-international-tax-form",
];

export const INTERNATIONAL_BANK_STREAM_VERIFICATION_METHOD =
  "ocr-international-bank-stream";

export const getNestedValue = (obj: any, path: string) =>
  path
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

export const getFirstValue = (obj: any, paths: string[]) => {
  const values = paths.map((path) => getNestedValue(obj, path));
  return values.find(
    (value) => value !== undefined && value !== null && value !== ""
  );
};

export const hasInternationalField = (value: any) =>
  value !== undefined && value !== null && value !== "";

export const isForeignCurrency = (value: any) =>
  hasInternationalField(value) && String(value).toUpperCase() !== "USD";

export const getInternationalFields = (stream: IncomeStream) => {
  const sources = [
    stream,
    ...(stream.incomes ?? []),
    ...(stream.incomes ?? []).map((income) => income.ocrResult),
  ];

  const getValue = (paths: string[]) => {
    const values = sources.map((source) => getFirstValue(source, paths));
    return values.find(
      (value) => value !== undefined && value !== null && value !== ""
    );
  };

  const country = getValue([
    "country",
    "countryName",
    "metadata.country",
    "metadata.countryName",
    "annotation.address.country",
    "captured.address.country",
    "captured.country",
  ]);

  const currency = getValue([
    "currency",
    "originalCurrency",
    "metadata.currency",
    "metadata.originalCurrency",
    "conversion.currency",
    "fx.currency",
  ]);

  const originalCurrency = getValue([
    "originalCurrency",
    "metadata.originalCurrency",
    "conversion.currency",
    "fx.currency",
  ]);

  const conversionRate = getValue([
    "conversionRate",
    "conversion_rate",
    "fxRate",
    "exchangeRate",
    "metadata.conversionRate",
    "metadata.conversion_rate",
    "conversion.rate",
    "fx.rate",
  ]);

  const conversionDate = getValue([
    "conversionDate",
    "conversion_date",
    "convertedAt",
    "exchangeDate",
    "metadata.conversionDate",
    "metadata.conversion_date",
    "conversion.date",
    "fx.date",
  ]);

  const originalAnnualAmount = getValue([
    "originalEstimatedAnnualGrossAmount",
    "originalEstimatedAnnualNetAmount",
  ]);

  const originalAmount = getValue([
    "originalGrossAmount",
    "originalNetAmount",
    "metadata.originalGrossAmount",
    "metadata.originalNetAmount",
  ]);

  const originalEstimatedAnnualGrossAmount = getValue([
    'originalEstimatedAnnualGrossAmount',
  ])

  const originalEstimatedAnnualNetAmount = getValue([
    'originalEstimatedAnnualNetAmount',
  ])

  return {
    country,
    currency,
    originalCurrency,
    conversionRate,
    conversionDate,
    originalAmount,
    originalAnnualAmount,
    originalEstimatedAnnualGrossAmount,
    originalEstimatedAnnualNetAmount,
  };
};

export const formatConversionRate = (value: any, decimalPlaces = 3) =>
  Number(value).toFixed(decimalPlaces);

export const isInternationalStream = (
  streamVerificationMethod: string,
  { currency, originalCurrency }: ReturnType<typeof getInternationalFields>
) =>
  INTERNATIONAL_STREAM_VERIFICATION_METHODS.includes(
    streamVerificationMethod
  ) ||
  isForeignCurrency(currency) ||
  isForeignCurrency(originalCurrency);

export const formatConversionDate = (value: any) => {
  const parsed = dayjs(value as string | number | Date);
  return parsed.isValid() ? parsed.format("MM/DD/YY") : undefined;
};

export const getInternationalVerificationLabel = (
  streamVerificationMethod: string,
  country?: string
) => {
  if (!country) return undefined;

  if (streamVerificationMethod === "ocr-international-paystubs") {
    return `Paystubs Upload (${country})`;
  }

  if (streamVerificationMethod === "ocr-international-tax-form") {
    return `Tax Forms Upload (${country})`;
  }

  if (streamVerificationMethod === "ocr-international-employment-letter") {
    return `Letter of Employment Upload (${country})`;
  }

  if (
    streamVerificationMethod === INTERNATIONAL_BANK_STREAM_VERIFICATION_METHOD
  ) {
    return `Bank Statement Upload (${country})`;
  }

  return undefined;
};

export const EMPLOYMENT_PROVIDER = {
  ARGYLE: "argyle",
  VERO: "vero",
  DOCUMENT: "document",
};

export function getExistingEmployments(employments) {
  return _.chain(employments)
    .filter((employment) => _.isEmpty(_.get(employment, "removedAt")))
    .value();
}

export function getCurrentEmployments(employments) {
  return _.chain(employments)
    .filter(
      (employment) =>
        !_.isEmpty(_.get(employment, "startDate")) &&
        _.isEmpty(_.get(employment, "endDate"))
    )
    .value();
}

export function getAutoLinkedEmployments(employments) {
  return _.chain(employments)
    .filter(
      (employment) =>
        _.get(employment, "provider") === EMPLOYMENT_PROVIDER.ARGYLE
    )
    .value();
}

export function getArgyleNonEmploymentIncomes(incomes) {
  return _.chain(incomes)
    .filter(
      (income) =>
        _.get(income, "provider") === "argyle" &&
        _.isEmpty(_.get(income, "employment")) &&
        _.get(income, "estimatedAnnualGrossAmount")
    )
    .value();
}

export const mapIncomeStreamVerificationMethodToSource = (
  method: StreamVerificationMethod
) => {
  switch (method) {
    case "argyle-paystubs":
    case "argyle-identity":
      return "Linked Employment";
    case "argyle-paystubs-benefits":
    case "argyle-identity-benefits":
      return "Linked Benefits";
    case "plaid-stream":
      return "Linked Bank Account";
    case "ocr-1099":
      return "1099 Upload";
    case "ocr-bank-stream":
    case "ocr-international-bank-stream":
      return "Bank Statement Upload";
    case "ocr-international-employment-letter":
      return "Letter of Employment Upload";
    case "ocr-international-paystubs":
      return "Paystubs Upload";
    case "ocr-international-tax-form":
      return "Tax Forms Upload";
    case "ocr-paystubs":
      return "Paystubs Upload";
    case "ocr-w2":
      return "W2 Upload";
    case "ocr-w2-paystubs":
      return "W2 & Paystubs Upload";
    case "stated":
      return "Stated";
    default:
      return "Unknown";
  }
};

export const mapIncomeStreamVerificationMethodToType = (
  method?: IncomeVerificationMethod,
  source?: Income["source"]
) => {
  switch (method) {
    case "argyle-paystub":
    case "ocr-paystub":
    case "ocr-international-paystub":
      return "Paystub";
    case "ocr-international-employment-letter":
      return "Letter of Employment";
    case "plaid-transaction":
      return "Bank Deposit";
    case "ocr-1099":
      return "1099";
    case "ocr-international-tax-form":
      return "Tax Form";
    case "ocr-w2":
      return "W2";
    case "ocr-transaction":
      return "Bank Deposit";
    default:
      switch (source) {
        case "international-paystub":
        case "paystub":
          return "Paystub";
        case "international-tax-form":
          return "Tax Form";
        case "international-letter-of-employment":
          return "Letter of Employment";
        case "bank-statement":
          return "Bank Deposit";
        default:
          return "Unknown";
      }
  }
};

export const editableStreamVerificationMethodType = (
  method?: IncomeVerificationMethod,
  source?: Income["source"]
) => {
  return (
    ["ocr-paystub", "ocr-international-paystub", "ocr-1099", "ocr-w2"].includes(
      method ?? ""
    ) ||
    (!method &&
      ["paystub", "international-paystub", "1099", "w2"].includes(source ?? ""))
  );
};
